import _LinkViewConfirmResetMutation from "../__generated__/LinkViewConfirmResetMutation.graphql";
import _LinkViewVerifyEmailMutation from "../__generated__/LinkViewVerifyEmailMutation.graphql";
import { CircleCheck, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { graphql, useMutation } from 'react-relay';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { LinkViewConfirmResetMutation } from '../__generated__/LinkViewConfirmResetMutation.graphql';
import type { LinkViewVerifyEmailMutation } from '../__generated__/LinkViewVerifyEmailMutation.graphql';
import { validatePassword, type FieldError } from '../profile/profile';
import { t } from '../i18n/t';
import { authFailureCopy } from '../session/refusals.ts';
import { refusalFor } from '../profile/authReason.ts';

/**
 * The two screens a mailed link opens.
 *
 * Both live here because they are the same screen with a different verb: a
 * token from a mailbox, one request, and an outcome the learner can act on.
 * Splitting them would duplicate the states and the copy that explains them.
 *
 * Neither is authorised by a cookie — the token is the whole credential — so
 * both work in a browser that has never signed in, which is the browser a mail
 * client opens.
 */

const VerifyEmail = (_LinkViewVerifyEmailMutation.hash && _LinkViewVerifyEmailMutation.hash !== "e3cd08da688e8afeaa514d0e87c501ad" && console.error("The definition of 'LinkViewVerifyEmailMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LinkViewVerifyEmailMutation);
const ConfirmReset = (_LinkViewConfirmResetMutation.hash && _LinkViewConfirmResetMutation.hash !== "2759a1d7fb52c81fed1105547b9f856c" && console.error("The definition of 'LinkViewConfirmResetMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LinkViewConfirmResetMutation);

/**
 * Take the token out of the address bar once it has been spent.
 *
 * It is single-use, so what is left in history is a link that no longer works
 * — but a URL that still carries a credential is one a learner can paste into
 * a chat window, and the browser sends it as a `Referer` to anything the page
 * loads.
 */
function forgetTheToken() {
  if (typeof history !== 'undefined' && typeof location !== 'undefined') {
    history.replaceState(null, '', location.pathname);
  }
}
type State = {
  step: 'working';
} | {
  step: 'done';
} | {
  step: 'failed';
  why: string;
};

/**
 * Confirm an address. Nothing to fill in: the link is the whole of it.
 *
 * The token comes from the query string rather than from a prop parsed out of
 * `window.location` by hand. `profile/link.ts` existed only to do that, and
 * is deleted with this change.
 */
export function VerifyView() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const onDone = () => navigate('/', {
    replace: true
  });
  const [state, setState] = useState<State>({
    step: 'working'
  });
  const [verifyEmail] = useMutation<LinkViewVerifyEmailMutation>(VerifyEmail);
  useEffect(() => {
    let live = true;
    verifyEmail({
      variables: {
        token
      },
      onCompleted: ({
        verifyEmail: result
      }) => {
        forgetTheToken();
        // A token spends once, so a second run — StrictMode's, or a stale
        // effect's — would report a working link as a broken one.
        if (!live) return;
        setState(result.ok ? {
          step: 'done'
        } : {
          step: 'failed',
          why: t(refusalFor(result.reason ?? 'LINK_REFUSED').message)
        });
      },
      onError: error => {
        if (live) setState({
          step: 'failed',
          why: t(authFailureCopy(error))
        });
      }
    });
    return () => {
      live = false;
    };
  }, [token, verifyEmail]);
  return <main className="mx-auto max-w-md p-6">
      {state.step === 'working' && <p className="text-sm text-base-content/60">{t('link-confirming')}</p>}
      {state.step === 'done' && <>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <CircleCheck className="size-6 text-success" aria-hidden="true" />
            {t('link-confirmed-title')}
          </h1>
          <p className="mt-1 text-sm text-base-content/60">{t('link-confirmed-body')}</p>
          <button className="btn btn-primary mt-6" onClick={onDone}>
            {t('link-sign-in')}
          </button>
        </>}
      {state.step === 'failed' && <>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <TriangleAlert className="size-6 text-warning" aria-hidden="true" />
            {t('link-failed-title')}
          </h1>
          <p className="mt-1 text-sm text-base-content/60">{state.why}</p>
          <p className="mt-4 text-sm text-base-content/60">{t('link-failed-help')}</p>
          <button className="btn btn-primary mt-6" onClick={onDone}>
            {t('link-back')}
          </button>
        </>}
    </main>;
}

/** Set a new password from a reset link. */
export function ResetView() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const onDone = () => navigate('/', {
    replace: true
  });
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [inFlight, setInFlight] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmReset] = useMutation<LinkViewConfirmResetMutation>(ConfirmReset);
  const submit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    // The same rule the server enforces, so the learner is told before the
    // round trip rather than after it. There is no address on this screen —
    // the token is what names the account — so it is the password rule
    // alone.
    const found = validatePassword(password);
    setErrors(found);
    if (found.length > 0) return;
    setInFlight(true);
    confirmReset({
      variables: {
        token,
        password
      },
      onCompleted: ({
        confirmPasswordReset: result
      }) => {
        setInFlight(false);
        if (result.ok) {
          forgetTheToken();
          setDone(true);
          return;
        }
        setErrors([{
          field: null,
          message: t(refusalFor(result.reason).message)
        }]);
      },
      onError: error => {
        setInFlight(false);
        setErrors([{
          field: null,
          message: t(authFailureCopy(error))
        }]);
      }
    });
  }, [confirmReset, password, token]);
  if (done) {
    return <main className="mx-auto max-w-md p-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <CircleCheck className="size-6 text-success" aria-hidden="true" />
          {t('link-reset-done-title')}
        </h1>
        <p className="mt-1 text-sm text-base-content/60">{t('link-reset-done-body')}</p>
        <button className="btn btn-primary mt-6" onClick={onDone}>
          {t('link-sign-in')}
        </button>
      </main>;
  }
  return <main className="mx-auto max-w-md p-6">
      <h1 className="text-2xl font-semibold">{t('link-reset-title')}</h1>
      <p className="mt-1 text-sm text-base-content/60">{t('link-reset-intro')}</p>

      <form className="mt-6 flex flex-col gap-4" onSubmit={submit}>
        <fieldset className="fieldset">
          <label className="label" htmlFor="new-password">
            {t('link-reset-password')}
          </label>
          <input id="new-password" type="password" autoComplete="new-password" className="input w-full" value={password} onChange={e => setPassword(e.target.value)} autoFocus />
        </fieldset>

        {errors.length > 0 && <ul className="alert alert-warning text-sm" role="alert">
            {errors.map(why => <li key={why.message} className="flex items-center gap-2">
                <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
                {why.message}
              </li>)}
          </ul>}

        <button className="btn btn-primary" type="submit" disabled={inFlight}>
          {inFlight ? t('common-saving') : t('link-reset-save')}
        </button>
      </form>
    </main>;
}